import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import ContentSpinner from '@/components/ContentSpinner';
import Text from '@/components/Text';
import { ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';
import { buildCalendar } from '@/utils/buildCalendar';
import { DistributionDepartmentsEnum, ReleaseStatusEnum } from '@/utils/enums';
import { hexToRgba } from '@/utils/theme';

import DistributionCalendarCard from './DistributionCalendarCard';

const Month = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.black, 0.03)};
  border-radius: 4px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const MonthButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

const MonthButtonsHolder = styled.div`
  display: grid;
  grid-template-columns: 32px 32px;
  grid-gap: 12px;
`;

const Calendar = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  margin-bottom: 80px;
`;

const CalendarHeader = styled.div`
  padding-bottom: 12px;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  justify-items: center;
`;

interface Props {
  activeStatus: ReleaseStatusEnum | null;
  activeDepartment: DistributionDepartmentsEnum;
  selectedChannels: number[];
  activeType: null | 0 | 1;
  activeReserved: null | 0 | 1;
}

const DistributionCalendar: FC<Props> = ({
  activeStatus,
  activeDepartment,
  selectedChannels,
  activeType,
  activeReserved,
}) => {
  const now = DateTime.now();

  const [selectedDay, setSelectedDay] = useState<DateTime>(now);

  const firstMothDay = selectedDay.startOf('month');

  const lastMonthDay = selectedDay.endOf('month');

  const calendar = buildCalendar(selectedDay);

  const getCurrentMonthName = () => {
    return selectedDay.toFormat('MMMM');
  };

  const getCurrentYear = () => {
    return selectedDay.toFormat('y');
  };

  const getPreviousMonth = () => {
    return selectedDay.minus({ months: 1 });
  };

  const getNextMonth = () => {
    return selectedDay.plus({ months: 1 });
  };

  const releasesCalendarQueryKey = [
    'releases',
    {
      // teamId,
      startDay: calendar.startDay,
      endDay: calendar.endDay,
      selectedChannels,
      activeDepartment,
      activeStatus,
      activeType,
      activeReserved,
    },
  ];

  const { data: releases, isLoading } = useQuery<ReleaseResource[]>(
    releasesCalendarQueryKey,
    () =>
      api.get<never, ReleaseResource[]>(`/v1/releases`, {
        params: {
          ...(activeType !== null && {
            'filter[isReel]': activeType,
          }),
          ...(activeReserved !== null && {
            'filter[isReserved]': activeReserved,
          }),
          ...(activeDepartment !== DistributionDepartmentsEnum.ALL && {
            'filter[is3rdParty]':
              activeDepartment === DistributionDepartmentsEnum.NATIVE
                ? '0'
                : '1',
          }),
          // ...(teamId !== "all" && {
          //   "filter[teamId]": teamId,
          // }),
          ...(!!selectedChannels.length && {
            'filter[channelId]': selectedChannels.join(','),
          }),
          'filter[status]': [
            ReleaseStatusEnum.WAITING,
            ReleaseStatusEnum.SCHEDULED,
            ReleaseStatusEnum.POSTED,
            ReleaseStatusEnum.REJECTED,
          ].join(','),
          'filter[since]': calendar.startDay.toISO(),
          'filter[until]': calendar.endDay.toISO(),
          ...(!!activeStatus && {
            'filter[status]': activeStatus,
          }),
        },
      }),
    {
      staleTime: 120 * 1000,
    },
  );

  const filteredReleases = (day: DateTime) =>
    releases?.filter(
      (item) =>
        item.releasedAt &&
        DateTime.fromISO(item.releasedAt).toLocaleString() ===
          day.toLocaleString(),
    );

  return (
    <>
      <Month>
        <Text $type="button">
          {getCurrentMonthName()} {getCurrentYear()}
        </Text>
        <MonthButtonsHolder>
          <MonthButton onClick={() => setSelectedDay(getPreviousMonth())}>
            <svg
              width="13"
              height="10"
              viewBox="0 0 13 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.43077 0.28214C4.82723 -0.102353 5.46032 -0.0926495 5.84481 0.303814C6.22931 0.700278 6.2196 1.33337 5.82314 1.71786L3.46995 4H12C12.5523 4 13 4.44772 13 5C13 5.55229 12.5523 6 12 6H3.46995L5.82314 8.28214C6.2196 8.66663 6.22931 9.29972 5.84481 9.69619C5.46032 10.0927 4.82723 10.1024 4.43077 9.71786L0.334781 5.74554C0.130085 5.56243 0.00125313 5.29626 0.00125313 5C0.00125313 4.70375 0.130079 4.43758 0.334767 4.25447L4.43077 0.28214Z"
                fill="currentColor"
              />
            </svg>
          </MonthButton>
          <MonthButton onClick={() => setSelectedDay(getNextMonth())}>
            <svg
              width="13"
              height="10"
              viewBox="0 0 13 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.56923 0.282139C8.17277 -0.102354 7.53968 -0.0926503 7.15519 0.303813C6.77069 0.700277 6.7804 1.33337 7.17686 1.71786L9.53005 4H1C0.447715 4 0 4.44772 0 5C0 5.55229 0.447715 6 1 6H9.53005L7.17686 8.28214C6.7804 8.66663 6.77069 9.29972 7.15519 9.69619C7.53968 10.0927 8.17277 10.1024 8.56923 9.71786L12.6652 5.74554C12.8699 5.56243 12.9987 5.29626 12.9987 5C12.9987 4.70375 12.8699 4.43758 12.6652 4.25447L8.56923 0.282139Z"
                fill="currentColor"
              />
            </svg>
          </MonthButton>
        </MonthButtonsHolder>
      </Month>
      {isLoading ? (
        <ContentSpinner />
      ) : (
        <>
          <CalendarHeader>
            <Text $type="smallHeadline">Monday</Text>
            <Text $type="smallHeadline">Tuesday</Text>
            <Text $type="smallHeadline">Wednesday</Text>
            <Text $type="smallHeadline">Thursday</Text>
            <Text $type="smallHeadline">Friday</Text>
            <Text $type="smallHeadline">Saturday</Text>
            <Text $type="smallHeadline">Sunday</Text>
          </CalendarHeader>
          <Calendar>
            {calendar.days.map((day: DateTime) => (
              <DistributionCalendarCard
                data={filteredReleases(day)}
                isToday={now.hasSame(day, 'day')}
                isOtherMonthDay={
                  day < firstMothDay ||
                  day > lastMonthDay ||
                  day.plus({ days: 1 }) < now
                }
                isDayBeforeToday={day.plus({ days: 1 }) < now}
                key={day.toISO()}
                day={day}
              />
            ))}
          </Calendar>
        </>
      )}
    </>
  );
};

export default DistributionCalendar;
